import {
  Box,
  Checkbox,
  CornerIndicator,
  Radio,
  Text,
  WizardStepRenderParams,
  useOnNext,
} from '@factorialco/deprecated-design-system'
import { AnimatePresence } from 'framer-motion'
import { TrainingsTrainingRequestScheduleEnum } from 'generated/resources/graphql'

import DayPicker from 'modules/core/components/DayPicker'
import MoneyField from 'modules/core/components/MoneyField'
import Selector from 'modules/core/components/Selector'
import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { track } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import type { WizardData } from 'modules/trainings/components/Shared/WizardComponent/types'

type Schedule = {
  label: string
  value: string
}
type Props = {
  amplitudeEventNext: string
  allowEmployeePayment?: boolean
}

export const TimingAndCostStep = ({
  data,
  onDataChanged,
  amplitudeEventNext,
  allowEmployeePayment,
}: WizardStepRenderParams<WizardData> & Props) => {
  const { company } = useCurrentSession()
  const scheduleOptions: Schedule[] = [
    {
      label: i18n.t('trainings.training_requests.create.schedule_options.during_work_hours'),
      value: 'workinghours',
    },
    {
      label: i18n.t('trainings.training_requests.create.schedule_options.mixed_working_hours'),
      value: 'mixedhours',
    },
    {
      label: i18n.t('trainings.training_requests.create.schedule_options.not_sure'),
      value: 'notsure',
    },
    {
      label: i18n.t('trainings.training_requests.create.schedule_options.outside_working_hours'),
      value: 'outsideworkinghours',
    },
  ]

  useOnNext(() => {
    track(amplitudeEventNext, {
      from: 'my_request',
    })
  })

  return (
    <Box width="s600" gap="s24">
      <Box gap="s16">
        <Text size="400" weight="medium" color="grey1000">
          {i18n.t('trainings.training_requests.create.timing_cost_step.title')}
        </Text>
        <Text size="300" weight="normal" color="grey1000">
          {i18n.t('trainings.training_requests.create.timing_cost_step.description')}
        </Text>
      </Box>

      <DayPicker
        label={i18n.t('trainings.training_requests.create.dates')}
        value={[data.startDate, data.endDate]}
        mode="range"
        onChange={(value) => onDataChanged({ ...data, startDate: value[0], endDate: value[1] })}
        placeholder={'from - to'}
      />
      <Box gap="s4">
        <Text size="200" weight="medium" color="grey1000">
          {i18n.t('trainings.training_requests.create.training_hours')}
        </Text>
        <Box flexDirection="row" gap="s8">
          <TextField
            type="number"
            placeholder={i18n.t('trainings.training_requests.create.duration_hours_placeholder')}
            onChange={(value) => onDataChanged({ ...data, duration: Number(value) })}
          />
          <TextField
            type="number"
            placeholder={i18n.t('trainings.training_requests.create.duration_minutes_placeholder')}
            onChange={(value) => onDataChanged({ ...data, minutes: Number(value) })}
          />
        </Box>
      </Box>
      <Selector.Popover
        label={i18n.t('trainings.training_requests.create.training_schedule')}
        placeholder={i18n.t('trainings.training_requests.create.select_placeholder')}
        searchPlaceholder={i18n.t('common.search')}
        blankLabel=""
        value={[data.schedule]}
        onChange={(selection) =>
          onDataChanged({ ...data, schedule: selection as TrainingsTrainingRequestScheduleEnum })
        }
      >
        {scheduleOptions.map((option, index) => (
          <Selector.Option
            key={index}
            value={option.value}
            label={option.label}
            searchValue={option.label}
          >
            {option.label}
          </Selector.Option>
        ))}
      </Selector.Popover>

      <Box width="fit-content">
        <MoneyField
          label={i18n.t('trainings.training_requests.create.cost')}
          currency={company.currency ?? ''}
          mandatory={allowEmployeePayment}
          value={data.cost ? String(data.cost) : ''}
          placeholder="0"
          onChange={(value) => onDataChanged({ ...data, cost: Number(value) })}
        />
      </Box>
      <AnimatePresence>
        {/* Ask who will pay for the training */}
        {!!data.cost && allowEmployeePayment ? (
          <Box.Motion
            gap="s16"
            initial={{ height: 0 }}
            animate={{ height: 'auto' }}
            exit={{ height: 0 }}
          >
            <Box position="relative" width="fit-content" paddingRight="s8">
              <Text.Normal weight="semibold">
                {i18n.t('trainings.training_requests.create.payment_options.label')}
              </Text.Normal>
              <CornerIndicator
                missing={!data.paymentOption}
                missingLabel={i18n.t('common.select_default')}
                attachmentPosition="top right"
                errors={false}
              />
            </Box>

            <Box gap="s8">
              <Radio
                name="payment-option"
                value={data.paymentOption ?? ''}
                onChange={(value) => onDataChanged({ ...data, paymentOption: value })}
                description={i18n.t(
                  'trainings.training_requests.create.payment_options.from_salary.description'
                )}
                option={{
                  label: i18n.t(
                    'trainings.training_requests.create.payment_options.from_salary.title'
                  ),
                  value: 'employee',
                }}
              />
              <AnimatePresence>
                {/* Specify payment installments */}
                {data.paymentOption === 'employee' && (
                  <Box.Motion
                    width="fit-content"
                    paddingX="s16"
                    paddingY="s16"
                    initial={{ height: 0 }}
                    animate={{ height: 'auto' }}
                    exit={{ height: 0 }}
                  >
                    <Selector.Popover
                      label={i18n.t('trainings.training_requests.create.number_of_payslips')}
                      value={[data.paymentInstallments ?? 1]}
                      blankLabel=""
                      placeholder=""
                      searchPlaceholder={i18n.t('common.search')}
                      onChange={(value) =>
                        onDataChanged({ ...data, paymentInstallments: Number(value) })
                      }
                    >
                      {Array.from({ length: 12 }).map((_, index) => (
                        <Selector.Option
                          key={index}
                          value={index + 1}
                          label={`${index + 1}`}
                          searchValue={`${index + 1}`}
                        >
                          {`${index + 1}`}
                        </Selector.Option>
                      ))}
                    </Selector.Popover>
                  </Box.Motion>
                )}
              </AnimatePresence>
            </Box>

            <Radio
              name="payment-option"
              value={data.paymentOption ?? ''}
              onChange={(value) => onDataChanged({ ...data, paymentOption: value })}
              description={i18n.t(
                'trainings.training_requests.create.payment_options.from_salary.description'
              )}
              option={{
                label: i18n.t(
                  'trainings.training_requests.create.payment_options.by_company.title'
                ),
                value: 'company',
              }}
            />
          </Box.Motion>
        ) : (
          <>
            <Box flexDirection="row" gap="s8" alignItems="center">
              <Checkbox
                checked={data.subsidized}
                onChange={() => onDataChanged({ ...data, subsidized: !data.subsidized })}
                size="large"
              />
              <Text size="200" weight="medium" color="grey800">
                {i18n.t('trainings.training_requests.create.subsidized_checkbox_text')}
              </Text>
            </Box>
          </>
        )}
      </AnimatePresence>
    </Box>
  )
}
